import React, { useState, useEffect } from 'react';
import { 
  Upload, 
  FileText, 
  Trash2, 
  CheckCircle2, 
  AlertCircle, 
  File,
  FileType,
  Presentation,
  Inbox,
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';

const API_URL = process.env.REACT_APP_API_URL || process.env.VITE_API_URL || 'http://localhost:8000';
const UPLOAD_PROGRESS_STORAGE_KEY = 'documents-upload-progress';

function Documents() {
  const { getToken } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [uploading, setUploading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, documentId: null, documentName: '' });
  const [isDragging, setIsDragging] = useState(false);
  
  // Load upload progress from localStorage on mount
  const [uploadProgress, setUploadProgress] = useState(() => {
    try {
      const saved = localStorage.getItem(UPLOAD_PROGRESS_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Filter out old completed uploads (older than 10 seconds)
        // Only keep processing uploads or very recently completed ones
        const now = Date.now();
        const filtered = {};
        Object.keys(parsed).forEach(uploadId => {
          const progress = parsed[uploadId];
          // Keep if processing, or if completed less than 10 seconds ago
          if (progress.status === 'processing' || 
              (progress.completedAt && (now - progress.completedAt) < 10000)) {
            filtered[uploadId] = progress;
          }
        });
        return filtered;
      }
    } catch (err) {
      console.error('Failed to load upload progress from localStorage:', err);
    }
    return {};
  });
  
  // Save upload progress to localStorage whenever it changes
  useEffect(() => {
    try {
      // Clean up old completed uploads before saving
      const now = Date.now();
      const cleaned = {};
      Object.keys(uploadProgress).forEach(uploadId => {
        const progress = uploadProgress[uploadId];
        // Keep if processing, or if completed less than 10 seconds ago (for persistence during navigation)
        // Completed uploads are removed from UI after 5 seconds, but kept in storage briefly for navigation
        if (progress.status === 'processing' || 
            (progress.completedAt && (now - progress.completedAt) < 10000)) {
          cleaned[uploadId] = progress;
        }
      });
      localStorage.setItem(UPLOAD_PROGRESS_STORAGE_KEY, JSON.stringify(cleaned));
    } catch (err) {
      console.error('Failed to save upload progress to localStorage:', err);
    }
  }, [uploadProgress]);
  
  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const abortControllerRef = React.useRef(null);
  const lastLoadParamsRef = React.useRef({ page: null, search: null }); // Cache last load params

  const loadDocuments = React.useCallback(async (page = 1, search = '', retryCount = 0, silent = false) => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    // Only set loading on initial call, not retries, and not for silent updates
    if (retryCount === 0 && !silent) {
      setLoading(true);
    }
    
    try {
      // Get token - Clerk should auto-refresh if expired
      const token = await getToken();
      if (!token) {
        if (!silent) {
          setLoading(false);
        }
        return;
      }
      
      const offset = (page - 1) * pageSize;
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: offset.toString()
      });
      if (search.trim()) {
        params.append('search', search.trim());
      }
      
      const response = await fetch(`${API_URL}/api/documents?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal: abortController.signal, // Add abort signal
      });
      
      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }
      
      // Handle 401 - token expired, try refreshing with a fresh token
      if (response.status === 401 && retryCount === 0) {
        // Wait longer to allow Clerk to refresh token (500ms instead of 100ms)
        await new Promise(resolve => setTimeout(resolve, 500));
        // Check if aborted before retry
        if (!abortController.signal.aborted) {
          // Force Clerk to refresh by getting token with skipCache option
          // This ensures we get a fresh token, not a cached expired one
          const freshToken = await getToken({ skipCache: true });
          if (freshToken && freshToken !== token) {
            // Only retry if we got a different token
            return loadDocuments(page, search, 1);
          }
          // If token is same or null, user might need to re-authenticate
          if (!silent) {
            console.warn('Token refresh failed - user may need to sign in again');
          }
        }
        return;
      }
      
      // If still 401 after retry, skip silently (token might be invalid)
      if (response.status === 401) {
        if (!silent) {
          setLoading(false);
        }
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        // Check if request was aborted before updating state
        if (abortController.signal.aborted) {
          return;
        }
        // Handle both old format (array) and new format (object with documents array)
        if (Array.isArray(data)) {
          setDocuments(data);
          setTotalDocuments(data.length);
        } else {
          setDocuments(data.documents || []);
          setTotalDocuments(data.total || 0);
        }
      }
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return;
      }
      console.error('Failed to load documents:', err);
    } finally {
      // Only set loading to false if this request wasn't aborted and not silent
      if (!abortController.signal.aborted && !silent) {
        setLoading(false);
      }
    }
  }, [getToken, pageSize]);

  useEffect(() => {
    // OPTIMIZATION: Skip reload if same params (prevents reload on every visit)
    const paramsKey = `${currentPage}-${searchQuery}`;
    const lastKey = `${lastLoadParamsRef.current.page}-${lastLoadParamsRef.current.search}`;
    
    if (paramsKey !== lastKey || documents.length === 0) {
      // Only load if params changed or no documents loaded
      lastLoadParamsRef.current = { page: currentPage, search: searchQuery };
      loadDocuments(currentPage, searchQuery);
    }
  }, [loadDocuments, currentPage, searchQuery, documents.length]);
  
  // Handle search on Enter key press (not while typing)
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
        setCurrentPage(1); // Reset to first page on new search
      }
    }
  };
  
  // Clear search when input is emptied
  useEffect(() => {
    // Only clear search if input is empty and search query exists
    if (searchInput === '' && searchQuery !== '') {
      setSearchQuery('');
      setCurrentPage(1);
    }
  }, [searchInput, searchQuery]);
  
  // Cleanup: cancel any in-flight requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Reliable status polling: Polls document status directly for all processing items
  // This runs continuously while there are processing items in uploadProgress
  useEffect(() => {
    let intervalId = null;
    let isChecking = false;
    
    const pollStatuses = async () => {
      // Prevent concurrent checks
      if (isChecking) return;
      
      // Get current processing items
      const currentProgress = uploadProgressRef.current || {};
      const processingItems = Object.entries(currentProgress).filter(
        ([_, progress]) => 
          (progress.status === 'processing' || progress.status === 'uploading') && 
          progress.documentId &&
          !progress.completedAt
      );
      
      // Stop if no processing items
      if (processingItems.length === 0) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      
      isChecking = true;
      try {
        const token = await getToken();
        if (!token) {
          isChecking = false;
          return;
        }
        
        // Check all processing items in parallel
        const checks = processingItems.map(async ([uploadId, progress]) => {
          try {
            const response = await fetch(`${API_URL}/api/documents/${progress.documentId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const doc = await response.json();
              // Update status if changed
              setUploadProgress(prev => {
                const current = prev[uploadId];
                if (!current || current.documentId !== progress.documentId) {
                  return prev; // Entry no longer exists or changed
                }
                
                const updated = { ...prev };
                if (doc.status === 'indexed' && current.status !== 'success') {
                  updated[uploadId] = {
                    ...current,
                    status: 'success',
                    progress: 100,
                    message: 'Uploaded and indexed successfully',
                    completedAt: Date.now()
                  };
                  return updated;
                } else if (doc.status === 'failed' && current.status !== 'error') {
                  updated[uploadId] = {
                    ...current,
                    status: 'error',
                    progress: 100,
                    message: 'Indexing failed',
                    completedAt: Date.now()
                  };
                  return updated;
                }
                return prev;
              });
            }
          } catch (err) {
            // Silently fail - will retry
          }
        });
        
        await Promise.all(checks);
      } finally {
        isChecking = false;
      }
    };
    
    // Check immediately
    pollStatuses();
    
    // Then poll every 2 seconds
    intervalId = setInterval(pollStatuses, 2000);
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [getToken, uploadProgress]); // Depend on uploadProgress to restart when new uploads are added
  
  // Unified status checker: Sync upload progress with document status from list
  // This syncs status from the documents list to upload progress
  // The dedicated polling mechanism handles direct API checks
  useEffect(() => {
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      let hasChanges = false;
      
      // Update existing upload progress entries based on document status from list
      Object.keys(newProgress).forEach(uploadId => {
        const progress = newProgress[uploadId];
        
        // Skip if already completed
        if (progress.status === 'success' || progress.status === 'error') {
          return;
        }
        
        // If we have a documentId, check document status from list
        if (progress.documentId) {
          const doc = documents.find(d => d.id === progress.documentId);
          
          if (doc) {
            // Update status based on document status
            if (doc.status === 'indexed' && progress.status !== 'success') {
              newProgress[uploadId] = {
                ...progress,
                status: 'success',
                progress: 100,
                message: 'Uploaded and indexed successfully',
                completedAt: Date.now()
              };
              hasChanges = true;
            } else if (doc.status === 'failed' && progress.status !== 'error') {
              newProgress[uploadId] = {
                ...progress,
                status: 'error',
                progress: 100,
                message: 'Indexing failed',
                completedAt: Date.now()
              };
              hasChanges = true;
            }
          }
        }
      });
      
      return hasChanges ? newProgress : prev;
    });
  }, [documents]);

  // Auto-remove only successful uploads from UI after 5 seconds
  // Failed uploads should remain until manually dismissed
  useEffect(() => {
    const successfulUploads = Object.entries(uploadProgress).filter(
      ([_, progress]) => progress.status === 'success' && progress.completedAt
    );
    
    if (successfulUploads.length === 0) return;
    
    // Set timeouts to remove each successful upload after 5 seconds
    const timeouts = successfulUploads.map(([uploadId, progress]) => {
      const timeSinceCompletion = Date.now() - (progress.completedAt || Date.now());
      const delay = Math.max(0, 5000 - timeSinceCompletion); // 5 seconds from completion
      
      return setTimeout(() => {
        setUploadProgress(prev => {
          // Double-check it's still successful before removing
          const current = prev[uploadId];
          if (current && current.status === 'success') {
            const newProgress = { ...prev };
            delete newProgress[uploadId];
            return newProgress;
          }
          return prev;
        });
      }, delay);
    });
    
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [uploadProgress]);
  
  // Function to manually dismiss an upload progress item
  const dismissUpload = (uploadId) => {
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[uploadId];
      return newProgress;
    });
  };

  // Refs to track current state without causing effect restarts
  const documentsRef = React.useRef(documents);
  const uploadProgressRef = React.useRef(uploadProgress);
  
  // Update refs when state changes
  React.useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);
  
  React.useEffect(() => {
    uploadProgressRef.current = uploadProgress;
  }, [uploadProgress]);

  // Simple polling: Check for processing documents and reload if needed
  useEffect(() => {
    let intervalId = null;
    let isPolling = false;
    let consecutiveFailures = 0; // Track consecutive failures to avoid spam
    let shouldContinuePolling = true; // Flag to control polling
    
    const poll = async () => {
      if (isPolling || !shouldContinuePolling) return;
      
      // Skip polling if we've had too many consecutive failures (likely auth issues)
      if (consecutiveFailures >= 3) {
        shouldContinuePolling = false;
        return;
      }
      
      isPolling = true;
      try {
        // Silently reload documents to check for status updates
        await loadDocuments(currentPage, searchQuery, 0, true);
        consecutiveFailures = 0; // Reset on success
        
        // After reloading, check if we should continue polling
        // Wait a bit longer to ensure state has updated from the reload
        setTimeout(() => {
          const currentDocs = documentsRef.current || [];
          const currentProgress = uploadProgressRef.current || {};
          
          const hasProcessingDocs = currentDocs.some(doc => doc.status === 'processing');
          const hasProcessingUploads = Object.values(currentProgress).some(
            p => (p.status === 'processing' || p.status === 'uploading') && !p.completedAt
          );
          
          // Stop polling if nothing is processing
          // Note: We check for completedAt to ensure we don't stop polling for items that just completed
          // but haven't been updated yet
          if (!hasProcessingDocs && !hasProcessingUploads) {
            shouldContinuePolling = false;
            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
          } else {
            // Still processing, continue polling
            shouldContinuePolling = true;
          }
        }, 200); // Increased delay to ensure state updates
      } catch (err) {
        consecutiveFailures++;
        // Silently handle errors - don't spam console
        if (consecutiveFailures >= 3) {
          console.warn('Polling stopped due to repeated failures');
          shouldContinuePolling = false;
        }
      } finally {
        isPolling = false;
      }
    };
    
    // Check initial state - use refs to avoid dependency issues
    // Refs are updated via separate useEffect hooks, so they're always current
    const hasProcessingDocs = documentsRef.current.some(doc => doc.status === 'processing');
    const hasProcessingUploads = Object.values(uploadProgressRef.current).some(
      p => (p.status === 'processing' || p.status === 'uploading') && !p.completedAt
    );
    
    // Always start polling if we have upload progress entries that aren't completed
    // This ensures we catch status changes even if documents list hasn't loaded yet
    if (hasProcessingDocs || hasProcessingUploads || Object.keys(uploadProgressRef.current).length > 0) {
      // Start polling every 2 seconds for faster updates
      intervalId = setInterval(poll, 2000);
      // Do immediate check
      poll();
    }
    
    return () => {
      shouldContinuePolling = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [currentPage, searchQuery, loadDocuments]); // Using refs for documents and uploadProgress to avoid dependency issues

  const validateFile = (file) => {
    const validTypes = ['.pdf', '.docx', '.pptx'];
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!validTypes.includes(ext)) {
      return { valid: false, error: `File type ${ext} is not supported. Please upload PDF, DOCX, or PPTX files.` };
    }
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      return { valid: false, error: `File ${file.name} is too large. Maximum size is 50MB.` };
    }
    return { valid: true };
  };

  const uploadFile = async (file, uploadId) => {
    const validation = validateFile(file);
    if (!validation.valid) {
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        newProgress[uploadId] = { 
          fileName: file.name,
          status: 'error', 
          message: validation.error 
        };
        return newProgress;
      });
      return;
    }

    setUploadProgress(prev => {
      const newProgress = { ...prev };
      newProgress[uploadId] = { 
        fileName: file.name,
        status: 'uploading', 
        progress: 0,
        startedAt: Date.now()  // Track when upload started
      };
      return newProgress;
    });

    // Helper function to upload with token refresh retry
    const uploadWithRetry = async (retryCount = 0) => {
      // Get token - Clerk should auto-refresh if expired
      const token = await getToken();
      if (!token) {
        setStatus({ type: 'error', message: 'Authentication required. Please sign in.' });
        return null;
      }
      
      // Recreate FormData on each attempt since it can only be read once
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      // Handle 401 - token expired, try refreshing with longer delay
      if (response.status === 401 && retryCount === 0) {
        // Wait longer to allow Clerk to refresh token (500ms instead of 100ms)
        await new Promise(resolve => setTimeout(resolve, 500));
        // Force Clerk to refresh by getting token with skipCache option
        // This ensures we get a fresh token, not a cached expired one
        const freshToken = await getToken({ skipCache: true });
        if (freshToken && freshToken !== token) {
          // Only retry if we got a different token
          return uploadWithRetry(1);
        }
        // If token is same or null, fail
        throw new Error('Authentication failed. Please sign in again.');
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || `Upload failed: ${response.status}`);
      }

      return response;
    };

    try {
      const response = await uploadWithRetry();
      if (!response) return; // Auth error already handled

      const responseData = await response.json();
      
      // Store document ID for status tracking
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        if (newProgress[uploadId]) {
          newProgress[uploadId] = {
            ...newProgress[uploadId],
            status: 'processing',
            progress: 50,
            documentId: responseData.id,  // Store document ID for status updates
            startedAt: Date.now()  // Track when upload started
          };
        }
        return newProgress;
      });
      
      // Don't start individual status checker - rely on unified document list polling
      // This prevents API overload from multiple concurrent status checkers
    } catch (err) {
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        if (newProgress[uploadId]) {
          newProgress[uploadId] = {
            ...newProgress[uploadId],
            status: 'error',
            message: err.message
          };
        }
        return newProgress;
      });
    }
  };

  const handleFiles = async (files) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    setUploading(true);
    setLoading(true);
    setStatus({ type: '', message: '' });
    
    // Create unique IDs for each file upload
    const uploadIds = fileArray.map((_, index) => `upload-${Date.now()}-${index}-${Math.random()}`);
    
    // Initialize ALL upload progress entries immediately so user sees all files
    setUploadProgress(() => {
      const initialProgress = {};
      fileArray.forEach((file, index) => {
        initialProgress[uploadIds[index]] = {
          fileName: file.name,
          status: 'uploading',
          progress: 0,
          startedAt: Date.now()
        };
      });
      return initialProgress;
    });

    // Upload files sequentially to avoid overwhelming the server
    for (let i = 0; i < fileArray.length; i++) {
      await uploadFile(fileArray[i], uploadIds[i]);
    }

    // Show summary status after all uploads complete
    // Use a callback to get the latest state
    setTimeout(() => {
      setUploadProgress(prev => {
        const successCount = Object.values(prev).filter(p => p.status === 'success').length;
        const errorCount = Object.values(prev).filter(p => p.status === 'error').length;
        
        if (errorCount === 0 && successCount > 0) {
          setStatus({ 
            type: 'success', 
            message: `Successfully uploaded ${successCount} file${successCount !== 1 ? 's' : ''}` 
          });
        } else if (successCount > 0) {
          setStatus({ 
            type: 'error', 
            message: `Uploaded ${successCount} file${successCount !== 1 ? 's' : ''}, ${errorCount} failed` 
          });
        } else if (errorCount > 0) {
          setStatus({ 
            type: 'error', 
            message: `Failed to upload ${errorCount} file${errorCount !== 1 ? 's' : ''}` 
          });
        }

        setUploading(false);
        setLoading(false);
        
        return prev; // Return unchanged state
      });
    }, 1000);
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    await handleFiles(files);
    e.target.value = '';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await handleFiles(files);
    }
  };

  const handleDeleteClick = (documentId, documentName) => {
    setDeleteModal({ isOpen: true, documentId, documentName });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, documentId: null, documentName: '' });
  };

  const handleDeleteConfirm = async () => {
    const { documentId } = deleteModal;
    
    try {
      const token = await getToken();
      if (!token) {
        setStatus({ type: 'error', message: 'Authentication required. Please sign in.' });
        return;
      }
      
      const response = await fetch(`${API_URL}/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Delete failed');
      }

      // Immediately remove from UI for better UX
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));

      setDeleteModal({ isOpen: false, documentId: null, documentName: '' });
      setStatus({ type: 'success', message: 'Document deleted successfully' });

      // Reload to ensure consistency
      setTimeout(() => {
        loadDocuments(currentPage, searchQuery);
      }, 200);
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
      setDeleteModal({ isOpen: false, documentId: null, documentName: '' });
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return <FileType style={{ width: '20px', height: '20px' }} />;
    if (ext === 'docx') return <File style={{ width: '20px', height: '20px' }} />;
    if (ext === 'pptx') return <Presentation style={{ width: '20px', height: '20px' }} />;
    return <FileText style={{ width: '20px', height: '20px' }} />;
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  return (
    <div className="container documents-page">
      <div className="page-header">
        <h2>Documents</h2>
        <p>Upload and manage documents for knowledge extraction</p>
      </div>

      <div className="card">
        <div className="card-header">
          <Upload className="card-icon" />
          <h3>Upload Document</h3>
        </div>
        <label 
          htmlFor="file-upload" 
          data-tour="upload-area"
          className={`file-upload-area ${uploading ? 'uploading' : ''} ${isDragging ? 'dragging' : ''}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="file-upload-icon">
            {uploading ? (
              <Loader2 style={{ width: '32px', height: '32px', animation: 'spin 0.8s linear infinite' }} />
            ) : (
              <Upload />
            )}
          </div>
          <div>
            <strong>{uploading ? 'Uploading...' : 'Click to upload'}</strong> or drag and drop
          </div>
          <div className="file-upload-text">PDF, DOCX, or PPTX files (Max 50MB per file)</div>
          <div className="file-upload-hint">You can select multiple files at once</div>
          <input
            id="file-upload"
            type="file"
            accept=".pdf,.docx,.pptx"
            onChange={handleUpload}
            disabled={loading}
            multiple
            style={{ display: 'none' }}
          />
        </label>
        
        {/* Upload Progress */}
        {Object.keys(uploadProgress).length > 0 && (
          <div className="upload-progress-list">
            {Object.entries(uploadProgress).map(([uploadId, progress]) => (
              <div key={uploadId} className={`upload-progress-item upload-progress-${progress.status}`}>
                <div className="upload-progress-header">
                  <span className="upload-progress-name">{progress.fileName}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className={`upload-progress-status status-${progress.status}`}>
                      {progress.status === 'uploading' && 'Uploading...'}
                      {progress.status === 'processing' && 'Processing...'}
                      {progress.status === 'success' && '✓ Complete'}
                      {progress.status === 'error' && '✗ Failed'}
                    </span>
                    {(progress.status === 'error' || progress.status === 'success') && (
                      <button
                        onClick={() => dismissUpload(uploadId)}
                        className="upload-progress-dismiss"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          color: 'var(--gray-500)',
                          borderRadius: '4px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--gray-100)';
                          e.currentTarget.style.color = 'var(--gray-700)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = 'var(--gray-500)';
                        }}
                        title="Dismiss"
                      >
                        <X style={{ width: '16px', height: '16px' }} />
                      </button>
                    )}
                  </div>
                </div>
                {progress.message && (
                  <div className="upload-progress-message">{progress.message}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {status.message && (
        <div className={`status ${status.type} status-animate`}>
          {status.type === 'success' ? (
            <CheckCircle2 style={{ width: '16px', height: '16px', flexShrink: 0 }} />
          ) : (
            <AlertCircle style={{ width: '16px', height: '16px', flexShrink: 0 }} />
          )}
          <span>{status.message}</span>
        </div>
      )}

      <div className="card">
        <div className="card-header" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '200px' }}>
            <FileText className="card-icon" />
            <h3 style={{ margin: 0 }}>
              Uploaded Documents
              {totalDocuments > 0 && (
                <span style={{ 
                  marginLeft: '8px', 
                  fontSize: '11px', 
                  fontWeight: '400', 
                  color: 'var(--gray-600)',
                  textTransform: 'none',
                  letterSpacing: 'normal'
                }}>
                  ({documents.filter(doc => doc.status === 'indexed').length} indexed of {totalDocuments})
                </span>
              )}
            </h3>
          </div>
          
          {/* Search Input */}
          <div style={{ 
            position: 'relative', 
            minWidth: '250px',
            maxWidth: '400px',
            flex: '0 1 auto'
          }}>
            <div style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}>
              {loading ? (
                <Loader2 style={{ 
                  width: '16px',
                  height: '16px',
                  color: 'var(--primary)',
                  animation: 'spin 0.8s linear infinite'
                }} />
              ) : (
                <Search style={{ 
                  width: '16px',
                  height: '16px',
                  color: 'var(--gray-500)'
                }} />
              )}
            </div>
            <input
              type="text"
              placeholder={loading ? "Searching..." : "Search documents... (Press Enter)"}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              disabled={loading}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                border: '1px solid var(--gray-300)',
                borderRadius: '6px',
                fontSize: '13px',
                outline: 'none',
                transition: 'border-color 0.2s',
                backgroundColor: loading ? 'var(--gray-50)' : 'white',
                cursor: loading ? 'wait' : 'text'
              }}
              onFocus={(e) => {
                if (!loading) {
                  e.target.style.borderColor = 'var(--primary)';
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--gray-300)';
              }}
            />
          </div>
        </div>
        {!loading && documents.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Inbox />
            </div>
            <div className="empty-state-text">
              {searchQuery ? 'No documents found' : 'No documents uploaded yet'}
            </div>
            <div className="empty-state-subtext">
              {searchQuery 
                ? `No documents match "${searchQuery}". Try a different search term.`
                : 'Upload your first document to get started'
              }
            </div>
          </div>
        ) : (
          <>
            {loading && documents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Loader2 style={{ width: '32px', height: '32px', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
                <p style={{ marginTop: '12px', color: 'var(--gray-600)' }}>Loading documents...</p>
              </div>
            ) : (
              <>
                {/* Loading overlay when documents exist but loading (page change/search) */}
                {loading && documents.length > 0 && (
                  <div style={{
                    position: 'relative',
                    marginBottom: '12px',
                    padding: '8px 12px',
                    backgroundColor: 'var(--gray-50)',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '13px',
                    color: 'var(--gray-700)',
                    border: '1px solid var(--gray-200)'
                  }}>
                    <Loader2 style={{ 
                      width: '14px', 
                      height: '14px', 
                      animation: 'spin 0.8s linear infinite',
                      color: 'var(--primary)'
                    }} />
                    <span>{searchQuery ? 'Searching...' : 'Loading...'}</span>
                  </div>
                )}
                <ul className="documents-list" style={{ opacity: loading && documents.length > 0 ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                  {documents.map((doc, idx) => (
              <li 
                key={doc.id} 
                className="document-item document-item-enter"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <div className="document-info">
                  <div className="document-icon">
                    {getFileIcon(doc.name)}
                  </div>
                  <div className="document-details">
                    <div className="document-name">{doc.name}</div>
                    <div className="document-meta">
                      <span className={`document-status status-${doc.status}`}>{doc.status}</span>
                      {doc.version > 1 && <span className="document-version">v{doc.version}</span>}
                      {(() => {
                        // Match the sorting logic: show last_indexed_at only if recent (within 24 hours) and more recent than uploaded_at
                        // Otherwise show uploaded_at to match sorting
                        const lastIndexed = doc.last_indexed_at ? new Date(doc.last_indexed_at) : null;
                        const uploaded = doc.uploaded_at ? new Date(doc.uploaded_at) : null;
                        const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
                        
                        let displayDate = null;
                        if (lastIndexed && uploaded) {
                          // Only use last_indexed_at if it's recent (within 24 hours) and more recent than uploaded_at
                          if (lastIndexed.getTime() > oneDayAgo && lastIndexed > uploaded) {
                            displayDate = lastIndexed;
                          } else {
                            displayDate = uploaded;
                          }
                        } else if (lastIndexed) {
                          // Only use last_indexed_at if it's recent, otherwise use uploaded_at
                          if (lastIndexed.getTime() > oneDayAgo) {
                            displayDate = lastIndexed;
                          } else if (uploaded) {
                            displayDate = uploaded;
                          } else {
                            displayDate = lastIndexed; // Fallback
                          }
                        } else if (uploaded) {
                          displayDate = uploaded;
                        }
                        
                        return displayDate ? (
                          <span className="document-updated">
                            Updated {formatTimeAgo(displayDate)}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
                <button
                  className="btn btn-danger"
                  onClick={() => handleDeleteClick(doc.id, doc.name)}
                >
                  <Trash2 style={{ width: '16px', height: '16px' }} />
                  <span>Delete</span>
                </button>
              </li>
                  ))}
                </ul>
                
                {/* Pagination Controls */}
                {totalDocuments > pageSize && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '24px',
                    paddingTop: '20px',
                    borderTop: '1px solid var(--gray-200)'
                  }}>
                    <div style={{ 
                      fontSize: '13px', 
                      color: 'var(--gray-600)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {loading && (
                        <Loader2 style={{ 
                          width: '14px', 
                          height: '14px', 
                          animation: 'spin 0.8s linear infinite',
                          color: 'var(--primary)'
                        }} />
                      )}
                      <span>
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalDocuments)} of {totalDocuments}
                      </span>
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      gap: '8px',
                      alignItems: 'center'
                    }}>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1 || loading}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 12px',
                          border: '1px solid var(--gray-300)',
                          borderRadius: '6px',
                          backgroundColor: currentPage === 1 || loading ? 'var(--gray-100)' : 'white',
                          color: currentPage === 1 || loading ? 'var(--gray-400)' : 'var(--gray-700)',
                          cursor: currentPage === 1 || loading ? 'not-allowed' : 'pointer',
                          fontSize: '13px',
                          transition: 'all 0.2s',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage > 1) {
                            e.target.style.backgroundColor = 'var(--gray-50)';
                            e.target.style.borderColor = 'var(--gray-400)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentPage > 1) {
                            e.target.style.backgroundColor = 'white';
                            e.target.style.borderColor = 'var(--gray-300)';
                          }
                        }}
                      >
                        <ChevronLeft style={{ width: '16px', height: '16px' }} />
                        <span>Previous</span>
                      </button>
                      
                      <div style={{
                        display: 'flex',
                        gap: '4px',
                        alignItems: 'center'
                      }}>
                        {Array.from({ length: Math.min(5, Math.ceil(totalDocuments / pageSize)) }, (_, i) => {
                          const totalPages = Math.ceil(totalDocuments / pageSize);
                          let pageNum;
                          
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              disabled={loading}
                              style={{
                                minWidth: '32px',
                                height: '32px',
                                padding: '0 8px',
                                border: '1px solid var(--gray-300)',
                                borderRadius: '6px',
                                backgroundColor: currentPage === pageNum ? 'var(--primary)' : 'white',
                                color: currentPage === pageNum ? 'white' : 'var(--gray-700)',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: currentPage === pageNum ? '600' : '400',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (currentPage !== pageNum) {
                                  e.target.style.backgroundColor = 'var(--gray-50)';
                                  e.target.style.borderColor = 'var(--gray-400)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (currentPage !== pageNum) {
                                  e.target.style.backgroundColor = 'white';
                                  e.target.style.borderColor = 'var(--gray-300)';
                                }
                              }}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalDocuments / pageSize), prev + 1))}
                        disabled={currentPage >= Math.ceil(totalDocuments / pageSize) || loading}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 12px',
                          border: '1px solid var(--gray-300)',
                          borderRadius: '6px',
                          backgroundColor: currentPage >= Math.ceil(totalDocuments / pageSize) ? 'var(--gray-100)' : 'white',
                          color: currentPage >= Math.ceil(totalDocuments / pageSize) ? 'var(--gray-400)' : 'var(--gray-700)',
                          cursor: currentPage >= Math.ceil(totalDocuments / pageSize) ? 'not-allowed' : 'pointer',
                          fontSize: '13px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage < Math.ceil(totalDocuments / pageSize)) {
                            e.target.style.backgroundColor = 'var(--gray-50)';
                            e.target.style.borderColor = 'var(--gray-400)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentPage < Math.ceil(totalDocuments / pageSize)) {
                            e.target.style.backgroundColor = 'white';
                            e.target.style.borderColor = 'var(--gray-300)';
                          }
                        }}
                      >
                        <span>Next</span>
                        <ChevronRight style={{ width: '16px', height: '16px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <AlertCircle style={{ width: '20px', height: '20px', color: 'var(--error)' }} />
              <h3 className="modal-title">Delete Document</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{deleteModal.documentName}</strong>?</p>
              <p className="modal-warning">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={handleDeleteCancel}
                type="button"
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger" 
                onClick={handleDeleteConfirm}
                type="button"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Documents;
